import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Payment } from "../../features/payments/models/Payment";
import { formatMoney } from "../../features/billing/utils/money";
import { paymentShapeLabel } from "../../features/payments/utils/paymentLabels";
import { ROUTES } from "../../routes";
import PaymentStatusBadge from "./PaymentStatusBadge";
import PaymentDetailRow from "./PaymentDetailRow";
import PaymentRowActions from "./PaymentRowActions";
import DebitStatusChip from "./DebitStatusChip";
import { getDisplayLocale } from "../../features/utils/timezoneUtils";

interface PaymentRowProps {
  payment: Payment;
  memberName: string;
  expanded: boolean;
  history: React.ComponentProps<typeof PaymentDetailRow>["history"];
  collectionReady: boolean;
  onToggleDetails: () => void;
  onFilterByContract: (contractId: string) => void;
  onVoid: () => void;
  onChanged: () => void;
  onError: (message: string | null) => void;
}

/** One payment row: status chips, member link, status-driven actions, expandable detail. */
const PaymentRow: React.FC<PaymentRowProps> = ({
  payment: p, memberName, expanded, history, collectionReady,
  onToggleDetails, onFilterByContract, onVoid, onChanged, onError,
}) => {
  const { t } = useTranslation();

  return (
    <>
      <tr
        onClick={onToggleDetails}
        className="cursor-pointer border-t border-grey-90 dark:border-primary-20 hover:bg-grey-95 dark:hover:bg-primary-20"
      >
        <td className="px-4 py-3">
          <PaymentStatusBadge status={p.status} />
          <DebitStatusChip status={p.latestDebitStatus} failureReason={p.latestDebitFailureReason} />
        </td>
        <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
          <Link
            to={ROUTES.MEMBER_DETAILS.replace(":id", p.memberId)}
            className="text-primary-40 hover:underline dark:text-primary-90"
          >
            {memberName}
          </Link>
        </td>
        <td className="px-4 py-3">
          {paymentShapeLabel(p.shape, t)}
          {p.contractId && (
            <button
              type="button"
              title={t("billing.payments.contractChipTitle")}
              onClick={(e) => {
                e.stopPropagation();
                onFilterByContract(p.contractId!);
              }}
              className="ml-2 rounded-full bg-grey-95 px-2 py-0.5 text-xs font-medium text-grey-30 hover:bg-grey-90 dark:bg-primary-20 dark:text-grey-70 dark:hover:bg-primary-30"
            >
              {t("billing.payments.contractChip")}
            </button>
          )}
        </td>
        <td className="px-4 py-3">{formatMoney(p.amountMinorUnits, p.amountCurrency, getDisplayLocale())}</td>
        <td className="px-4 py-3">
          {p.remainingBalance === undefined
            ? "—"
            : p.remainingBalance === 0
              ? t("billing.payments.usedUp")
              : t("billing.payments.balanceLeft", { count: p.remainingBalance })}
        </td>
        <td className="px-4 py-3">
          {p.dueDate ?? (p.billingPeriodStart ? `${p.billingPeriodStart} → ${p.billingPeriodEnd}` : "—")}
        </td>
        <td className="px-4 py-3 text-grey-30 dark:text-grey-70">{p.dateCreated.slice(0, 10)}</td>
        <td className="px-4 py-3 text-right whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
          <PaymentRowActions
            payment={p}
            collectionReady={collectionReady}
            onVoid={onVoid}
            onChanged={onChanged}
            onError={onError}
          />
        </td>
      </tr>
      {expanded && <PaymentDetailRow payment={p} history={history} colSpan={8} />}
    </>
  );
};

export default PaymentRow;
